import { type Row } from '@tanstack/react-table'
import { useCallback, useMemo } from 'react'
import {
  type FindingResponse,
  type FindingResultPaginatedResponse,
  type FindingResultResponse,
  type FixResultResponse,
  type PushResponse,
  type ResultsScaResultResponse,
  type ResultsTriageResultResponse,
  type Severity,
} from '../../utils/api-client/api-schemas'

/**
 * Unified row type for the scan findings table that supports both outer rows and expandable sub-rows.
 *
 * This type is intended to become the new row type for scan-findings-table, replacing the
 * direct use of `FindingResponse`. It's necessary because react-table's sub-rows concept
 * requires that outer rows (parent findings) and inner rows (representative/non-representative
 * results) share the same `RowData` type.
 *
 * **Type variants:**
 * - `'outer'`: Top-level finding row containing the complete `FindingResponse`
 * - `'representative'`: Sub-row containing the representative result for a finding
 * - `'non-representative'`: Sub-row containing a non-representative result for a finding
 *
 * **Architecture notes:**
 * - The discriminated union pattern allows type-safe handling of different row types
 * - Column renderers can use the `type` field to determine which data shape to expect
 * - Sub-rows are provided via `useGetSubRows` hook, which filters and transforms the data
 */
export type RowData =
  | {
      type: 'outer'
      data: FindingResponse
    }
  | {
      type: 'representative' | 'non-representative'
      data: SubRowData
    }

export type SubRowData = {
  analysisHref: string
  representative: boolean
  findingId: string
  parentSeverity: Severity | null | undefined
  parentEffectiveCvss: Severity | undefined
  parentBaseCvss: Severity | undefined
  pushState?: PushResponse | null
} & (
  | {
      type: 'sast'
      triage?: ResultsTriageResultResponse
      fix?: FixResultResponse
    }
  | {
      type: 'sca'
      sca: ResultsScaResultResponse
      triage?: ResultsTriageResultResponse
      fix?: FixResultResponse
    }
)

type SubRowsData = {
  findingId: string
  representative: SubRowData
  nonRepresentative: SubRowData[]
}

/**
 * Builds a single sub-row from an array of finding results for a specific analysis.
 *
 * This function transforms raw finding results (triage, fix, and/or SCA results) into a
 * structured `SubRowData` object. It validates that the results array contains a valid combination:
 * SAST results (0-1 triage + 0-1 fix) OR SCA results (0-1 sca + 0-1 fix).
 *
 * **Valid result combinations:**
 * - SAST: triage only, fix only, or both triage and fix
 * - SCA: sca only, fix only, or both sca and fix
 *
 * **Invalid combinations (throws error):**
 * - Multiple triage results
 * - Multiple fix results
 * - Multiple SCA results
 * - Both SAST (triage) and SCA results together
 * - Empty results array
 *
 * @param analysisHref - Link to the analysis that produced these results
 * @param results - Array of finding results from the API (triage, fix, and/or SCA)
 * @param representative - Whether this is the representative result for the finding
 * @param findingId - ID of the parent finding (needed for drawer callbacks)
 * @param parentSeverity - Severity of the parent finding (needed for severity display)
 * @param pushState - Latest push state of the parent finding (fallback for per-result push state)
 * @param parentEffectiveCvss - Effective (contextual) CVSS of the parent finding, for the CVSS column
 * @param parentBaseCvss - Base CVSS of the parent finding, used to detect a Pixee reduction
 * @returns SubRowData with either SAST or SCA data structure
 * @throws Error if the results array contains an invalid combination
 *
 * **Note**: Only exported for unit testing, not intended for direct use outside this module.
 */
export const buildSubRow = ({
  analysisHref,
  results,
  representative,
  findingId,
  parentSeverity,
  pushState,
  parentEffectiveCvss,
  parentBaseCvss,
}: {
  analysisHref: string
  results: FindingResultResponse[]
  representative: boolean
  findingId: string
  parentSeverity: Severity | null | undefined
  pushState?: PushResponse | null
  parentEffectiveCvss?: Severity
  parentBaseCvss?: Severity
}): SubRowData => {
  const message =
    'Each sub-row must contain 0-1 triage results and 0-1 fix results OR 0-1 sca results and 0-1 fix results'

  const triages = results.filter(result => result.type === 'triage')
  if (triages.length > 1) {
    throw new Error(message)
  }
  const fixes = results.filter(result => result.type === 'fix')
  if (fixes.length > 1) {
    throw new Error(message)
  }

  const scas = results.filter(result => result.type === 'sca')
  if (scas.length > 1) {
    throw new Error(message)
  }

  const triage = triages[0]
  const fix = fixes[0]
  const sca = scas[0]

  // Prefer the push state on the triage result itself (per-result, surfaced by the backend).
  // Fall back to the parent finding's push state for backwards compat.
  const resolvedPushState = triage?.latest_push_state ?? pushState

  if (sca) {
    return {
      type: 'sca',
      analysisHref,
      representative,
      findingId,
      parentSeverity,
      parentEffectiveCvss,
      parentBaseCvss,
      pushState: resolvedPushState,
      sca,
      triage,
      fix,
    }
  }

  if (triage || fix) {
    return {
      type: 'sast',
      analysisHref,
      representative,
      findingId,
      parentSeverity,
      parentEffectiveCvss,
      parentBaseCvss,
      pushState: resolvedPushState,
      triage,
      fix,
    }
  }

  throw new Error(message)
}

/**
 * Builds the complete sub-rows structure for a finding, including representative and non-representative results.
 *
 * This function takes a finding and its associated results, then groups them by analysis href to create
 * a structured representation with one representative sub-row and zero or more non-representative sub-rows.
 * The representative result is determined by the finding's embedded `representative-results` field.
 *
 * **Process:**
 * 1. Extracts the representative results and analysis href from the finding
 * 2. Groups all results by their analysis href
 * 3. Filters out the representative analysis to get non-representative results
 * 4. Transforms each group into a SubRowData using `buildSubRow`
 *
 * **Returns `undefined` when:**
 * - Finding is undefined or has no representative results
 * - Representative results have no analysis href
 * - This indicates the data is still loading or incomplete
 *
 * @param finding - The finding response from the API (may be undefined during loading)
 * @param results - Paginated response containing all results for the finding (may be undefined during loading)
 * @returns SubRowsData object with representative and non-representative sub-rows, or undefined if data is incomplete
 *
 * **Note**: Only exported for unit testing, not intended for direct use outside this module.
 */
export const buildSubRows = (
  finding: FindingResponse | undefined,
  results: FindingResultPaginatedResponse | undefined
): SubRowsData | undefined => {
  const representativeResults = finding?._embedded?.['representative-results']?._embedded.items
  if (!representativeResults) {
    return undefined
  }

  const representativeAnalysisHref = representativeResults[0]?._links.analysis.href
  if (!representativeAnalysisHref) {
    return undefined
  }

  const groupedResults =
    results?._embedded.items.reduce((previous, current) => {
      const analysisHref = current._links.analysis.href
      if (!previous.has(analysisHref)) {
        previous.set(analysisHref, [current])
      } else {
        previous.get(analysisHref)!.push(current)
      }

      return previous
    }, new Map<string, FindingResultResponse[]>()) ?? new Map<string, FindingResultResponse[]>()

  // Get the representative results from the full results list (not the embedded data)
  // to ensure we have complete data including timestamps
  const representativeFullResults = groupedResults.get(representativeAnalysisHref)
  if (!representativeFullResults) {
    return undefined
  }

  const pushState = finding.push_state
  return {
    findingId: finding.id,
    representative: buildSubRow({
      analysisHref: representativeAnalysisHref,
      results: representativeFullResults,
      representative: true,
      findingId: finding.id,
      parentSeverity: finding.severity,
      pushState,
      parentEffectiveCvss: finding.effective_cvss,
      parentBaseCvss: finding.base_cvss,
    }),
    nonRepresentative: [...groupedResults.entries()]
      .filter(([analysisHref]) => analysisHref !== representativeAnalysisHref)
      .map(([analysisHref, results]) =>
        buildSubRow({
          analysisHref,
          results,
          representative: false,
          findingId: finding.id,
          parentSeverity: finding.severity,
          pushState,
          parentEffectiveCvss: finding.effective_cvss,
          parentBaseCvss: finding.base_cvss,
        })
      ),
  }
}

/**
 * Builds sub-rows data from findings with embedded results.
 *
 * This hook transforms findings that have embedded `results` data into a Map of sub-rows.
 * It expects findings to be fetched with `embed: ['items.representative-results', 'items.results']`
 * so that both representative and full results are available without additional API calls.
 *
 * **Data flow:**
 * 1. Iterates over findings array
 * 2. For each finding with embedded results, builds SubRowsData using `buildSubRows`
 * 3. Returns a Map keyed by finding ID for efficient lookup
 *
 * **Key features:**
 * - No additional API calls: Uses pre-embedded data from the findings response
 * - Memoized: Only recomputes when findings array changes
 * - Efficient lookups: Map structure allows O(1) access by finding ID
 *
 * @param findings - Array of finding responses with embedded results data
 * @returns Map from finding ID to SubRowsData (may contain undefined values if embedded data is missing)
 */
export const useSubRowsFromEmbeddedData = (findings: FindingResponse[]) => {
  return useMemo(() => {
    const map = new Map<string, SubRowsData | undefined>()
    for (const finding of findings) {
      const embeddedResults = finding._embedded?.results
      const subRowsData = buildSubRows(finding, embeddedResults)
      map.set(finding.id, subRowsData)
    }
    return map
  }, [findings])
}

type SubRowsMap = Map<string, SubRowsData | undefined>

/**
 * Creates a memoized callback that retrieves sub-rows for a given row.
 *
 * This hook returns a stable callback function intended to be passed to react-table's
 * `getSubRows` option. It determines which sub-rows (representative and non-representative
 * results) should be displayed when a finding row is expanded.
 *
 * **Behavior:**
 * - Returns empty array for non-outer rows (sub-rows cannot have their own sub-rows)
 * - Returns empty array if there are no non-representative sub-rows (nothing to expand)
 * - Returns array with representative sub-row followed by non-representative sub-rows when expandable
 *
 * @param subRowsMap - Map of finding IDs to their sub-rows data, from `useSubRowsFromEmbeddedData`
 * @returns Memoized callback that takes a `RowData` and returns its sub-rows as `SubRowData[]`
 *
 * @example
 * ```tsx
 * const subRowsMap = useSubRowsFromEmbeddedData(findings)
 * const getSubRows = useGetSubRows(subRowsMap)
 *
 * const table = useReactTable({
 *   data: findings,
 *   columns,
 *   getSubRows, // Pass directly to react-table
 * })
 * ```
 */
export const useGetSubRows = (subRowsMap: SubRowsMap) =>
  useCallback(
    (row: RowData): RowData[] => {
      if (row.type !== 'outer') {
        return []
      }

      const subRowsData = subRowsMap.get(row.data.id)
      if (!subRowsData?.nonRepresentative.length) {
        return []
      }

      return [
        { type: 'representative' as const, data: subRowsData.representative },
        ...subRowsData.nonRepresentative.map(data => ({ type: 'non-representative' as const, data })),
      ]
    },
    [subRowsMap]
  )

/**
 * Creates a memoized callback that determines if a row can be expanded.
 *
 * This hook returns a stable callback function intended to be passed to react-table's
 * `getRowCanExpand` option. It controls which rows display the expand/collapse icon
 * by checking if a finding has non-representative results available.
 *
 * **Behavior:**
 * - Returns `false` for non-outer rows (sub-rows cannot be expanded)
 * - Returns `true` if the finding has at least one non-representative result
 * - Returns `false` if the finding has only a representative result (nothing to expand to)
 *
 * @param subRowsMap - Map of finding IDs to their sub-rows data, from `useSubRowsFromEmbeddedData`
 * @returns Memoized callback that takes a `RowData` and returns whether it can be expanded
 *
 * @example
 * ```tsx
 * const subRowsMap = useSubRowsFromEmbeddedData(findings)
 * const getRowCanExpand = useGetRowCanExpand(subRowsMap)
 *
 * const table = useReactTable({
 *   data: findings,
 *   columns,
 *   getSubRows,
 *   getRowCanExpand, // Pass directly to react-table
 * })
 * ```
 */
export const useGetRowCanExpand = (subRowsMap: SubRowsMap) =>
  useCallback(
    (row: Row<RowData>) =>
      row.original.type === 'outer' && !!subRowsMap.get(row.original.data.id)?.nonRepresentative.length,
    [subRowsMap]
  )
